import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Shield, AlertTriangle, Ban, Trash2, Check, FileText,
  User as UserIcon, Calendar, RefreshCw, ExternalLink, ImageIcon, History
} from 'lucide-react';
import api from '../lib/api';

/**
 * AdminReports — Trust & Safety dashboard.
 *
 * Premium minimal dark-friendly UI matching ASAP branding. Shows:
 *  - tab strip (Pending / Reviewed / Suspended / Removed / All)
 *  - report list (reporter, target, reason, severity, date, count of internal notes)
 *  - drawer/expandable detail with admin actions + internal notes
 */

const STATUSES = [
  { key: 'pending',   label: 'Pending' },
  { key: 'reviewed',  label: 'Reviewed' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'removed',   label: 'Removed' },
  { key: 'dismissed', label: 'Dismissed' },
  { key: 'all',       label: 'All' },
];

export default function AdminReports() {
  const navigate = useNavigate();
  const [authChecking, setAuthChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [status, setStatus] = useState('pending');
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);

  // Initial admin gate
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/admin/me');
        if (!alive) return;
        setIsAdmin(!!data.is_admin);
      } catch {
        setIsAdmin(false);
      }
      setAuthChecking(false);
    })();
    return () => { alive = false; };
  }, []);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get(`/admin/reports?status=${status}&limit=80`),
        api.get('/admin/stats'),
      ]);
      setReports(r.data.reports || []);
      setStats(s.data);
    } catch (e) {
      console.error('admin fetch err', e);
    }
    setLoading(false);
  }, [status]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchReports();
  }, [isAdmin, fetchReports]);

  if (authChecking) {
    return (
      <div className="min-h-[100dvh] bg-[#F7F6F2] flex items-center justify-center">
        <RefreshCw size={20} className="text-[#9B9EA3] animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-[100dvh] bg-[#F7F6F2] flex flex-col items-center justify-center px-6 text-center">
        <div className="w-14 h-14 rounded-2xl bg-[#FF3B30]/10 flex items-center justify-center mb-3">
          <Shield size={22} className="text-[#FF3B30]" />
        </div>
        <h1 className="text-lg font-extrabold text-[#111111]">Admin only</h1>
        <p className="text-sm text-[#6B6E73] mt-1.5">You don't have access to this area.</p>
        <button
          onClick={() => navigate('/')}
          className="mt-5 px-5 py-2.5 rounded-full bg-[#111111] text-white text-sm font-bold active:scale-95"
          data-testid="admin-no-access-back"
        >
          Back to home
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[#F7F6F2] pb-32" data-testid="admin-reports-page">
      {/* Header */}
      <div
        className="sticky top-0 z-30 bg-[#F7F6F2]/95 backdrop-blur-xl border-b border-black/[0.05]"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex items-center gap-2 px-3 pt-3 pb-2">
          <button
            data-testid="admin-back"
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-white border border-black/[0.06] flex items-center justify-center active:scale-90 transition-transform"
          >
            <ArrowLeft size={18} className="text-[#111111]" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Shield size={14} className="text-[#3F5200]" />
              <h1 className="text-[16px] font-extrabold text-[#111111] tracking-tight">Trust &amp; Safety</h1>
            </div>
            {stats && (
              <p className="text-[11px] text-[#9B9EA3] -mt-0.5">
                {stats.pending} pending · {stats.users_suspended} suspended · {stats.plans_removed} removed
              </p>
            )}
          </div>
          <button
            data-testid="admin-refresh"
            onClick={fetchReports}
            className="w-10 h-10 rounded-full bg-white border border-black/[0.06] flex items-center justify-center active:scale-90 transition-transform"
          >
            <RefreshCw size={16} className={`text-[#111111] ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Status chips */}
        <div className="px-3 pb-3 flex gap-2 overflow-x-auto hide-scrollbar" data-testid="admin-status-chips">
          {STATUSES.map(s => {
            const count = stats ? (s.key === 'all' ? stats.total : stats[s.key]) : null;
            return (
              <button
                key={s.key}
                data-testid={`admin-chip-${s.key}`}
                onClick={() => { setStatus(s.key); setOpenId(null); }}
                className={`feed-chip ${status === s.key ? 'feed-chip-active' : ''}`}
              >
                {s.label}{count !== null && count !== undefined ? ` · ${count}` : ''}
              </button>
            );
          })}
        </div>
      </div>

      {/* List */}
      <div className="px-4 pt-5 space-y-3" data-testid="admin-reports-list">
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-2xl border border-black/[0.06] p-4">
                <div className="skeleton h-3 w-24 mb-3" />
                <div className="skeleton h-4 w-3/4 mb-2" />
                <div className="skeleton h-3 w-1/2" />
              </div>
            ))}
          </div>
        )}

        {!loading && reports.length === 0 && (
          <div className="py-14 text-center" data-testid="admin-empty">
            <div className="w-14 h-14 rounded-2xl bg-[#B6E600]/15 flex items-center justify-center mx-auto mb-3">
              <Check size={22} className="text-[#3F5200]" />
            </div>
            <h3 className="text-[16px] font-bold text-[#111111]">All clear</h3>
            <p className="text-xs text-[#6B6E73] mt-1.5">No reports in this queue.</p>
          </div>
        )}

        {!loading && reports.map(r => (
          <ReportCard
            key={r.id}
            report={r}
            isOpen={openId === r.id}
            onToggle={() => setOpenId(openId === r.id ? null : r.id)}
            onAction={async (action, payload = {}) => {
              if (busy) return;
              setBusy(true);
              try {
                await api.post(`/admin/reports/${r.id}/action`, {
                  action,
                  reason: payload.reason || '',
                  guideline: payload.guideline || '',
                  next_consequence: payload.next_consequence || '',
                });
                await fetchReports();
                setOpenId(null);
              } catch (e) {
                alert(e.response?.data?.detail || 'Action failed');
              }
              setBusy(false);
            }}
            onAddNote={async (note) => {
              if (busy) return;
              setBusy(true);
              try {
                const { data } = await api.post(`/admin/reports/${r.id}/notes`, { note });
                setReports(prev => prev.map(rep =>
                  rep.id === r.id
                    ? { ...rep, admin_notes: [...(rep.admin_notes || []), data.note] }
                    : rep
                ));
              } catch (e) {
                alert(e.response?.data?.detail || 'Failed to add note');
              }
              setBusy(false);
            }}
            busy={busy}
          />
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  ReportCard — contextual moderation                                 */
/* ------------------------------------------------------------------ */

const GUIDELINES = [
  'Harassment or bullying',
  'Hate speech or discrimination',
  'Sexual or inappropriate content',
  'Spam or scam',
  'Stalking or unwanted contact',
  'Fake profile or impersonation',
  'Misleading or fake event',
  'Unsafe location or activity',
  'Violence or threats',
  'Other guideline violation',
];

function ReportCard({ report, isOpen, onToggle, onAction, onAddNote, busy }) {
  const [noteInput, setNoteInput] = useState('');
  const [actionMode, setActionMode] = useState(null); // null | 'warn' | 'suspend' | 'remove'
  const [actionReason, setActionReason] = useState('');
  const [actionGuideline, setActionGuideline] = useState(GUIDELINES[0]);
  const [actionConsequence, setActionConsequence] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const target = report.target || {};
  const reporter = report.reporter || {};
  const targetType = report.target_type;
  const isResolved = report.status !== 'pending';
  const severityColor = report.severity === 'high' ? '#FF3B30' : '#9B9EA3';
  const notes = report.admin_notes || [];

  // Lazy-load full detail (prior reports + recent actions) when the card opens
  useEffect(() => {
    if (!isOpen || detail || detailLoading) return;
    setDetailLoading(true);
    api.get(`/admin/reports/${report.id}`)
      .then(r => setDetail(r.data))
      .catch(() => setDetail({ prior_reports: [], recent_actions: [] }))
      .finally(() => setDetailLoading(false));
  }, [isOpen, detail, detailLoading, report.id]);

  const targetTitle =
    targetType === 'user'
      ? (target.display_name || target.username || target.id || 'User')
      : (target.title || 'Plan');

  const targetSubtitle =
    targetType === 'user'
      ? `@${target.username || '—'}${target.suspended ? ' · suspended' : ''}`
      : `${target.city || ''}${target.removed ? ' · removed' : ''}`;

  const deeplinkUrl = (detail && detail.deeplink_url) || report.deeplink_url || null;
  const priorReports = (detail && detail.prior_reports) || [];
  const recentActions = (detail && detail.recent_actions) || [];

  const submitAction = async (action) => {
    await onAction(action, {
      reason: actionReason.trim(),
      guideline: actionGuideline,
      next_consequence: actionConsequence.trim(),
    });
    setActionMode(null);
    setActionReason('');
    setActionConsequence('');
  };

  return (
    <div
      data-testid={`admin-report-${report.id}`}
      className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden"
    >
      <button
        onClick={onToggle}
        className="w-full text-left p-4 active:bg-[#F1F0EA]/40 transition-colors"
        data-testid={`admin-report-toggle-${report.id}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1.5">
              <span
                className="text-[10px] font-bold uppercase tracking-wider"
                style={{ color: severityColor }}
              >
                {report.severity === 'high' ? 'Severe' : 'Normal'}
              </span>
              <span className="text-[10px] text-[#9B9EA3]">·</span>
              <span className="text-[10px] font-semibold text-[#9B9EA3] uppercase tracking-wider">
                {targetType}
              </span>
              {isResolved && (
                <>
                  <span className="text-[10px] text-[#9B9EA3]">·</span>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-[#3F5200]">
                    {report.status}
                  </span>
                </>
              )}
            </div>
            <p className="text-sm font-bold text-[#111111] truncate">{targetTitle}</p>
            <p className="text-xs text-[#6B6E73] truncate">{targetSubtitle}</p>
            <p className="text-[12px] text-[#111111] mt-2 font-medium">
              {labelForReason(report.reason)}
            </p>
            {report.details && (
              <p className="text-xs text-[#6B6E73] mt-1 line-clamp-2">{report.details}</p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            <span className="text-[10px] text-[#9B9EA3]">{formatDate(report.created_at)}</span>
            {notes.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#F1F0EA] rounded-full text-[10px] font-bold text-[#6B6E73]">
                <FileText size={10} /> {notes.length}
              </span>
            )}
          </div>
        </div>
      </button>

      {isOpen && (
        <div className="border-t border-black/[0.05] bg-[#FAF9F4]" data-testid={`admin-report-detail-${report.id}`}>
          {/* Rich target preview */}
          <div className="p-4 border-b border-black/[0.05]">
            <p className="text-[10.5px] font-bold uppercase tracking-wider text-[#9B9EA3] mb-2">Reported {targetType}</p>
            <TargetPreview target={target} targetType={targetType} />
            {deeplinkUrl && (
              <a
                href={deeplinkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#111111] text-white text-[12px] font-bold active:scale-95 transition-transform"
                data-testid={`admin-view-in-app-${report.id}`}
              >
                <ExternalLink size={13} /> View in app
              </a>
            )}
          </div>

          {/* Reporter */}
          <div className="p-4 border-b border-black/[0.05]">
            <p className="text-[10.5px] font-bold uppercase tracking-wider text-[#9B9EA3] mb-1.5">Reporter</p>
            <div className="flex items-center gap-2 text-sm">
              <UserIcon size={13} className="text-[#9B9EA3]" />
              <span className="font-semibold text-[#111111]">{reporter.display_name || '—'}</span>
              <span className="text-[#9B9EA3]">@{reporter.username || '—'}</span>
            </div>
            {report.details && (
              <p className="mt-2 text-[13px] text-[#111111] leading-relaxed bg-white border border-black/[0.06] rounded-xl px-3 py-2">{report.details}</p>
            )}
          </div>

          {/* Prior reports + actions history */}
          {(priorReports.length > 0 || recentActions.length > 0) && (
            <div className="p-4 border-b border-black/[0.05]" data-testid={`admin-history-${report.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <History size={13} className="text-[#9B9EA3]" />
                <p className="text-[10.5px] font-bold uppercase tracking-wider text-[#9B9EA3]">History</p>
              </div>
              {priorReports.length > 0 && (
                <div className="space-y-1.5 mb-2">
                  {priorReports.map(p => (
                    <div key={p.id} className="flex items-center justify-between text-[12px] bg-white border border-black/[0.06] rounded-lg px-3 py-1.5">
                      <span className="truncate">
                        <span className="font-bold text-[#111111]">{labelForReason(p.reason)}</span>
                        <span className="text-[#9B9EA3]"> · {p.status}</span>
                      </span>
                      <span className="text-[10px] text-[#9B9EA3] flex-shrink-0">{formatDate(p.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
              {recentActions.length > 0 && (
                <div className="space-y-1.5">
                  {recentActions.map((a, i) => (
                    <div key={i} className="flex items-center justify-between text-[12px] bg-white border border-black/[0.06] rounded-lg px-3 py-1.5">
                      <span className="truncate">
                        <span className="font-bold text-[#3F5200]">{a.action}</span>
                        <span className="text-[#9B9EA3]"> by {a.performed_by_name || 'admin'}</span>
                      </span>
                      <span className="text-[10px] text-[#9B9EA3] flex-shrink-0">{formatDate(a.performed_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Internal notes */}
          <div className="p-4 border-b border-black/[0.05]">
            <p className="text-[10.5px] font-bold uppercase tracking-wider text-[#9B9EA3] mb-2">Internal notes ({notes.length})</p>
            <div className="space-y-2">
              {notes.map((n) => (
                <div key={n.id} className="bg-white border border-black/[0.06] rounded-xl px-3 py-2">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[11px] font-bold text-[#111111]">{n.author_name}</p>
                    <p className="text-[10px] text-[#9B9EA3]">{formatDate(n.created_at)}</p>
                  </div>
                  <p className="text-[12px] text-[#4B4F55] leading-relaxed whitespace-pre-wrap">{n.note}</p>
                </div>
              ))}
              <div className="flex gap-2">
                <input
                  data-testid={`admin-note-input-${report.id}`}
                  value={noteInput}
                  onChange={e => setNoteInput(e.target.value)}
                  placeholder="Add internal note…"
                  className="flex-1 bg-white text-[#111111] rounded-xl px-3 py-2 text-[13px] border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/40 placeholder-[#B0B3B8]"
                />
                <button
                  data-testid={`admin-note-add-${report.id}`}
                  onClick={async () => {
                    if (!noteInput.trim()) return;
                    await onAddNote(noteInput.trim());
                    setNoteInput('');
                  }}
                  disabled={busy || !noteInput.trim()}
                  className="px-4 py-2 rounded-xl bg-[#111111] text-white text-[12px] font-bold active:scale-95 disabled:opacity-40"
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* Actions */}
          {!isResolved && (
            <div className="p-4">
              {actionMode ? (
                <ActionForm
                  mode={actionMode}
                  guideline={actionGuideline}
                  setGuideline={setActionGuideline}
                  reason={actionReason}
                  setReason={setActionReason}
                  consequence={actionConsequence}
                  setConsequence={setActionConsequence}
                  onSubmit={() => submitAction(actionMode)}
                  onCancel={() => setActionMode(null)}
                  busy={busy}
                  testId={report.id}
                />
              ) : (
                <>
                  <p className="text-[10.5px] font-bold uppercase tracking-wider text-[#9B9EA3] mb-2">Take action</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => onAction('dismiss', { reason: '' })}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#111111] text-white text-[12px] font-bold active:scale-95 transition-transform disabled:opacity-50"
                      data-testid={`admin-action-dismiss-${report.id}`}
                    >
                      <Check size={13} /> Dismiss
                    </button>
                    {targetType === 'user' && (
                      <>
                        <button
                          onClick={() => setActionMode('warn')}
                          disabled={busy}
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#FFD60A] text-[#111111] text-[12px] font-bold active:scale-95 transition-transform disabled:opacity-50"
                          data-testid={`admin-action-warn-${report.id}`}
                        >
                          <AlertTriangle size={13} /> Warn user
                        </button>
                        <button
                          onClick={() => setActionMode('suspend')}
                          disabled={busy}
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#FF3B30] text-white text-[12px] font-bold active:scale-95 transition-transform disabled:opacity-50"
                          data-testid={`admin-action-suspend-${report.id}`}
                        >
                          <Ban size={13} /> Suspend user
                        </button>
                      </>
                    )}
                    {targetType === 'plan' && (
                      <button
                        onClick={() => setActionMode('remove')}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#FF3B30] text-white text-[12px] font-bold active:scale-95 transition-transform disabled:opacity-50"
                        data-testid={`admin-action-remove-${report.id}`}
                      >
                        <Trash2 size={13} /> Remove plan
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {isResolved && (
            <div className="p-4 flex items-center gap-2 text-[12px] text-[#6B6E73]">
              <Calendar size={13} />
              Resolved {formatDate(report.resolved_at)} · action: <span className="font-bold text-[#111111]">{report.resolution_action || report.status}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TargetPreview({ target, targetType }) {
  if (!target || !target.id) {
    return <p className="text-xs text-[#9B9EA3]">Target unavailable.</p>;
  }
  if (targetType === 'plan') {
    return (
      <div className="flex gap-3">
        {target.cover_image ? (
          <img loading="lazy" decoding="async" src={target.cover_image} alt="" className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
        ) : (
          <div className="w-20 h-20 rounded-xl bg-[#F1F0EA] flex items-center justify-center flex-shrink-0">
            <ImageIcon size={20} className="text-[#9B9EA3]" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-[#111111] truncate">{target.title}</p>
          <p className="text-[11px] text-[#6B6E73] truncate">
            {[target.venue, target.city].filter(Boolean).join(' · ') || '—'}
          </p>
          {target.description && (
            <p className="text-xs text-[#6B6E73] mt-1.5 line-clamp-2">{target.description}</p>
          )}
          <p className="text-[10px] text-[#9B9EA3] mt-1.5">
            Hosted by{' '}
            <span className="font-semibold text-[#111111]">{target.host_name || '—'}</span>
            {target.host_username && <span> @{target.host_username}</span>}
          </p>
        </div>
      </div>
    );
  }
  // user
  return (
    <div className="flex gap-3 items-center">
      {target.profile_photo ? (
        <img loading="lazy" decoding="async" src={target.profile_photo} alt="" className="w-14 h-14 rounded-full object-cover flex-shrink-0" />
      ) : (
        <div className="w-14 h-14 rounded-full bg-[#F1F0EA] flex items-center justify-center flex-shrink-0">
          <UserIcon size={20} className="text-[#9B9EA3]" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-[#111111] truncate">{target.display_name || target.username}</p>
        <p className="text-[11px] text-[#6B6E73] truncate">
          @{target.username || '—'}{target.city ? ` · ${target.city}` : ''}
        </p>
        {target.bio && <p className="text-xs text-[#6B6E73] mt-1 line-clamp-2">{target.bio}</p>}
        <div className="flex items-center gap-3 mt-1.5 text-[10px] text-[#9B9EA3]">
          <span><b className="text-[#111111]">{target.report_count || 0}</b> reports</span>
          <span><b className="text-[#111111]">{target.warning_count || 0}</b> warnings</span>
          {target.suspended && <span className="text-[#FF3B30] font-bold">SUSPENDED</span>}
        </div>
      </div>
    </div>
  );
}

function ActionForm({ mode, guideline, setGuideline, reason, setReason, consequence, setConsequence, onSubmit, onCancel, busy, testId }) {
  const titles = {
    warn: 'Warn user',
    suspend: 'Suspend user',
    remove: 'Remove plan',
  };
  const submitLabels = {
    warn: 'Issue warning',
    suspend: 'Suspend',
    remove: 'Remove',
  };
  const tone = mode === 'warn' ? 'bg-[#FFD60A] text-[#111111]' : 'bg-[#FF3B30] text-white';

  return (
    <div className="space-y-2.5" data-testid={`admin-action-form-${mode}-${testId}`}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-[#111111]">{titles[mode]}</p>

      <div>
        <p className="text-[10px] font-semibold text-[#9B9EA3] uppercase tracking-wider mb-1">Guideline violated</p>
        <select
          value={guideline}
          onChange={e => setGuideline(e.target.value)}
          className="w-full bg-white text-[#111111] rounded-xl px-3 py-2 text-[13px] border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/40"
          data-testid={`admin-form-guideline-${testId}`}
        >
          {GUIDELINES.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>

      <div>
        <p className="text-[10px] font-semibold text-[#9B9EA3] uppercase tracking-wider mb-1">Internal reason (audit log only)</p>
        <input
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Why this action — for audit log"
          className="w-full bg-white text-[#111111] rounded-xl px-3 py-2 text-[13px] border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/40 placeholder-[#B0B3B8]"
          data-testid={`admin-form-reason-${testId}`}
        />
      </div>

      <div>
        <p className="text-[10px] font-semibold text-[#9B9EA3] uppercase tracking-wider mb-1">What happens next (shown to user)</p>
        <textarea
          value={consequence}
          onChange={e => setConsequence(e.target.value)}
          placeholder="Optional — leave blank to use the default warning message."
          rows={2}
          className="w-full bg-white text-[#111111] rounded-xl px-3 py-2 text-[13px] border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/40 placeholder-[#B0B3B8] resize-none"
          data-testid={`admin-form-consequence-${testId}`}
        />
      </div>

      <div className="flex gap-2 pt-1">
        <button
          onClick={onCancel}
          disabled={busy}
          className="flex-1 py-2.5 rounded-full bg-[#F1F0EA] text-[#111111] text-[12px] font-bold active:scale-95 disabled:opacity-50"
          data-testid={`admin-form-cancel-${testId}`}
        >
          Cancel
        </button>
        <button
          onClick={onSubmit}
          disabled={busy}
          className={`flex-[2] py-2.5 rounded-full ${tone} text-[12px] font-bold active:scale-95 disabled:opacity-50`}
          data-testid={`admin-form-submit-${testId}`}
        >
          {submitLabels[mode]}
        </button>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diffH = (Date.now() - d.getTime()) / 3600000;
  if (diffH < 1) return `${Math.max(1, Math.round(diffH * 60))}m ago`;
  if (diffH < 24) return `${Math.round(diffH)}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const REASON_LABELS = {
  harassment: 'Harassment or bullying',
  fake_profile: 'Fake or misleading profile',
  inappropriate: 'Inappropriate behavior',
  spam: 'Spam or promotional content',
  scam: 'Scam or fraud',
  hate_speech: 'Hate speech or discrimination',
  stalking: 'Stalking or unwanted contact',
  fake_event: 'Fake or misleading event',
  unsafe: 'Unsafe location or activity',
  csam: 'Child sexual content',
  violence: 'Violence or threats',
  impersonation: 'Impersonation',
  other: 'Other',
};

function labelForReason(reason) {
  return REASON_LABELS[reason] || reason;
}
